import { Component } from "@theme/component";

/**
 * A tabbed interface for product detail panels inside the drawer.
 * Handles tab switching, pre-selection via data-active-tab, and
 * keyboard navigation following WAI-ARIA tabs pattern.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabButtons - Tab navigation buttons
 * @property {HTMLElement[]} tabPanels - Tab content panels
 *
 * @extends {Component<Refs>}
 */
export default class ProductTabs extends Component {
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    // Check for pre-selected tab from drawer trigger
    const mount = this.closest("[data-active-tab]");
    if (mount) {
      const index = parseInt(mount.dataset.activeTab, 10);
      if (!Number.isNaN(index)) this.#activeIndex = index;
    }

    this.#activateTab(this.#activeIndex);
    this.addEventListener("click", this.#handleClick);
    this.addEventListener("keydown", this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeydown);
  }

  #handleClick = (event) => {
    const button = event.target.closest("[role='tab']");
    if (!button) return;
    const index = parseInt(button.dataset.tabIndex, 10);
    if (!Number.isNaN(index)) this.#activateTab(index);
  };

  #handleKeydown = (event) => {
    if (!event.target.closest("[role='tab']")) return;
    const { tabButtons } = this.refs;
    const count = tabButtons.length;

    if (event.key === "ArrowRight") {
      this.#activateTab((this.#activeIndex + 1) % count);
    } else if (event.key === "ArrowLeft") {
      this.#activateTab((this.#activeIndex - 1 + count) % count);
    }
  };

  #activateTab(index) {
    const { tabButtons, tabPanels } = this.refs;
    if (!tabButtons?.length || !tabPanels?.length) return;

    tabButtons.forEach((btn, i) => {
      const isActive = i === index;
      btn.setAttribute("aria-selected", String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
      btn.classList.toggle("text-primary-medium-13", isActive);
      btn.classList.toggle("underline", isActive);
      btn.classList.toggle("text-primary-13", !isActive);
    });

    tabPanels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.#activeIndex = index;
    tabButtons[index]?.focus();
  }
}

if (!customElements.get("product-tabs")) {
  customElements.define("product-tabs", ProductTabs);
}
